import type { FlowPieceKind, RestorePlan, RestoreResult } from '@arcnet/contracts';
import { useRestoreRoute } from '../../../lib/api/hooks-tms.js';
import { Modal } from '../../../components/ui/Modal.js';
import { Icon } from '../../../components/ui/Icon.js';
import { KIND_LABEL } from './model.js';
import type { Names } from './describe.js';
import { StepChanges } from './HistoryList.js';
import { clock, shortAt } from './history.js';

/**
 * Restoring a route to a version (docs/41 §12). It says what changes from now, step by step, and
 * what else that touches. Two of the owner's rules (7 October 2026) shape it: numbers are never moved
 * back, only mentioned; and a version that needs anything since deleted is refused, naming each one.
 */

const WHAT: Record<RestorePlan['missing'][number]['what'], string> = {
  extension: 'Extension', mailbox: 'Voicemail box', recording: 'Recording', schedule: 'Schedule',
  ivr: 'IVR menu', ring_group: 'Hunt list', queue: 'Queue', message: 'Message', hours: 'Hours check',
};

export function RestoreDialog({ tenantId, routeId, routeName, versionId, at, plan, names, onClose, onRestored }: {
  tenantId: string; routeId: string; routeName: string; versionId: string; at: string;
  plan: RestorePlan; names: Names; onClose: () => void; onRestored: (r: RestoreResult) => void;
}) {
  const restore = useRestoreRoute(tenantId);
  const when = shortAt(at);

  if (plan.missing.length > 0) {
    return (
      <Modal title={`${routeName} can’t be restored to ${when}`} size="sm" onClose={onClose}>
        <div className="arc-confirm arc-history-confirm">
          <p className="arc-muted arc-confirm-body">That version needs things that have since been deleted:</p>
          <ul className="arc-history-missing">
            {plan.missing.map((m, i) => (
              <li key={i}>
                <Icon name="warning" size={13} />
                <span>
                  {m.name ? <>{WHAT[m.what]} <strong>{m.name}</strong></> : <>A {WHAT[m.what].toLowerCase()}</>}, used by {m.used_by}
                </span>
              </li>
            ))}
          </ul>
          <p className="arc-muted arc-confirm-body">Nothing has changed. Pick another version, or change the routing by hand.</p>
          <div className="arc-confirm-actions">
            <button type="button" className="arc-btn arc-btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </Modal>
    );
  }

  if (plan.changes.length === 0) {
    return (
      <Modal title={`${routeName} is already like this`} size="sm" onClose={onClose}>
        <div className="arc-confirm">
          <p className="arc-muted arc-confirm-body">The routing now is the same as the version of {when}. There is nothing to restore.</p>
          <div className="arc-confirm-actions">
            <button type="button" className="arc-btn arc-btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </Modal>
    );
  }

  const flows = plan.mode === 'flows';
  const word = (k: FlowPieceKind) => KIND_LABEL[k].toLowerCase();
  return (
    <Modal title={`Restore ${routeName} to ${when}?`} size="md" onClose={onClose}>
      <div className="arc-confirm arc-history-confirm">
        <p className="arc-muted arc-confirm-body">
          {flows
            ? 'Opens as a draft for you to check. Nothing changes for callers until you publish it.'
            : 'Applies at once: calls follow it within a minute. The restore is a version of its own, so it can be undone the same way.'}
        </p>
        <h3 className="arc-history-h">What changes from now</h3>
        <StepChanges steps={plan.changes} names={names} note={(s) => (s.shared_with.length > 0 ? (
          <p className="arc-history-note">
            <Icon name="info" size={13} />
            <span><strong>{s.shared_with.join(', ')}</strong> {s.shared_with.length === 1 ? 'uses' : 'use'} this {word(s.kind)} too,
              so {flows ? 'publishing' : 'restoring'} changes it there as well.</span>
          </p>
        ) : null)} />
        {plan.numbers.map((n) => (
          <p key={n.e164} className="arc-history-note">
            <Icon name="info" size={13} />
            <span>
              {n.e164} was on this route then and is {n.now_on ? <>on <strong>{n.now_on}</strong></> : 'on no route'} now.
              A restore never moves numbers, so it stays where it is.
            </span>
          </p>
        ))}
        {flows && plan.draft && (
          <p className="arc-history-note" data-tone="warn">
            <Icon name="warning" size={13} />
            <span>
              Your unpublished draft ({plan.draft.changes} {plan.draft.changes === 1 ? 'change' : 'changes'}, saved {clock(plan.draft.updated_at)}) is
              replaced by this restore.
            </span>
          </p>
        )}
        {restore.error !== null && <p className="arc-error" style={{ margin: 0 }}>{(restore.error as Error).message}</p>}
        <div className="arc-confirm-actions">
          <button type="button" className="arc-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="arc-btn arc-btn-primary" disabled={restore.isPending}
            onClick={() => restore.mutate({ routeId, versionId }, { onSuccess: onRestored })}>
            {restore.isPending ? 'Restoring…' : flows ? 'Restore as draft' : 'Restore now'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
